import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface ZySplitViewCaretOptions {
    enabled: boolean;
}

type SharedSelection = { from: number; to: number } | null;

export const SPLIT_VIEW_CARET_META_KEY = 'zy-split-view-caret';
const splitViewCaretPluginKey = new PluginKey<SharedSelection>(SPLIT_VIEW_CARET_META_KEY);

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zySplitViewCaret: {
            applySharedSelection: (selection: { from: number; to: number } | { type: 'node'; pos: number }) => ReturnType;
            clearSharedSelection: () => ReturnType;
        };
    }
}

function buildCaretWidget(): HTMLElement {
    const caret = document.createElement('span');
    caret.className = 'zy-split-view-caret';
    return caret;
}

function setSharedSelectionMeta(view: EditorView, value: SharedSelection) {
    view.dispatch(view.state.tr.setMeta(SPLIT_VIEW_CARET_META_KEY, { value }));
}

type Rect = { top: number; bottom: number };
function scrollableAncestor(el: HTMLElement): HTMLElement | null {
    let n: HTMLElement | null = el.parentElement;
    while (n) {
        const { overflowY } = getComputedStyle(n);
        if ((overflowY === 'auto' || overflowY === 'scroll') && n.scrollHeight > n.clientHeight) {
            return n;
        }
        n = n.parentElement;
    }
    return null;
}
export function scrollMirrorCaretIntoScrollParent(view: EditorView, rect: Rect): void {
    const margin = 100;
    const root = scrollableAncestor(view.dom);
    if (root?.dataset.splitScrollLocked === 'true') return;
    if (root) {
        const box = root.getBoundingClientRect();
        if (rect.bottom > box.bottom - margin) {
            root.scrollTop += rect.bottom - (box.bottom - margin);
        } else if (rect.top < box.top + margin) {
            root.scrollTop += rect.top - (box.top + margin);
        }
        return;
    }
    const vh = window.innerHeight;
    if (rect.bottom > vh - margin) {
        window.scrollBy(0, rect.bottom - (vh - margin));
    } else if (rect.top < margin) {
        window.scrollBy(0, rect.top - margin);
    }
}

export const ZySplitViewCaret = Extension.create<ZySplitViewCaretOptions>({
    name: 'zySplitViewCaret',

    addOptions() {
        return { enabled: false };
    },

    addCommands() {
        return {
            applySharedSelection:
                selection =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;
                    let from: number;
                    let to: number;
                    if ('type' in selection && selection.type === 'node') {
                        from = selection.pos;
                        to = selection.pos;
                    } else {
                        from = (selection as { from: number; to: number }).from;
                        to = (selection as { from: number; to: number }).to;
                    }
                    const maxPos = tr.doc.content.size;
                    from = Math.min(Math.max(from, 0), maxPos);
                    to = Math.min(Math.max(to, 0), maxPos);
                    tr.setMeta(SPLIT_VIEW_CARET_META_KEY, { value: { from, to } });
                    if (!tr.selection.empty) {
                        const collapsed = TextSelection.create(tr.doc, tr.selection.anchor);
                        tr.setSelection(collapsed);
                    }
                    dispatch(tr);
                    return true;
                },
            clearSharedSelection:
                () =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;
                    dispatch(tr.setMeta(SPLIT_VIEW_CARET_META_KEY, { value: null }));
                    return true;
                },
        };
    },

    addProseMirrorPlugins() {
        if (!this.options.enabled) return [];

        let editorHasFocus = true;
        let decorationsReady = false;

        return [
            new Plugin<SharedSelection>({
                key: splitViewCaretPluginKey,

                state: {
                    init() {
                        return null;
                    },
                    apply(tr, prev) {
                        const meta = tr.getMeta(SPLIT_VIEW_CARET_META_KEY);
                        if (meta !== undefined) return meta.value as SharedSelection;
                        if (prev === null) return null;
                        const maxPos = tr.doc.content.size;
                        return {
                            from: Math.min(prev.from, maxPos),
                            to: Math.min(prev.to, maxPos),
                        };
                    },
                },

                props: {
                    decorations(state) {
                        if (!decorationsReady || editorHasFocus) return DecorationSet.empty;

                        const sel = splitViewCaretPluginKey.getState(state);
                        if (!sel) return DecorationSet.empty;

                        const decorations: Decoration[] = [];
                        const { from, to } = sel;

                        decorations.push(
                            Decoration.widget(to, buildCaretWidget, {
                                key: 'zy-split-caret',
                                side: 1,
                            }),
                        );

                        if (from !== to) {
                            const lo = Math.min(from, to);
                            const hi = Math.max(from, to);
                            decorations.push(
                                Decoration.inline(lo, hi, {
                                    class: 'zy-split-view-selection',
                                }),
                            );
                        }

                        return DecorationSet.create(state.doc, decorations);
                    },
                },

                view(view) {
                    editorHasFocus = view.hasFocus();
                    decorationsReady = true;
                    view.updateState(view.state);

                    const onFocus = () => {
                        editorHasFocus = true;
                        if (splitViewCaretPluginKey.getState(view.state) !== null) {
                            setSharedSelectionMeta(view, null);
                        } else {
                            view.updateState(view.state);
                        }
                    };

                    const onBlur = () => {
                        editorHasFocus = false;
                        view.updateState(view.state);
                    };

                    view.dom.addEventListener('focus', onFocus);
                    view.dom.addEventListener('blur', onBlur);

                    return {
                        update: (updatedView: EditorView) => {
                            editorHasFocus = updatedView.hasFocus();

                            if (editorHasFocus) {
                                if (splitViewCaretPluginKey.getState(updatedView.state) !== null) {
                                    setSharedSelectionMeta(updatedView, null);
                                }
                                return;
                            }

                            const shared = splitViewCaretPluginKey.getState(updatedView.state);
                            if (shared) {
                                requestAnimationFrame(() => {
                                    scrollMirrorCaretIntoScrollParent(updatedView, updatedView.coordsAtPos(shared.to, 1));
                                });
                            }
                        },
                        destroy() {
                            decorationsReady = false;
                            editorHasFocus = true;
                            view.dom.removeEventListener('focus', onFocus);
                            view.dom.removeEventListener('blur', onBlur);
                        },
                    };
                },
            }),
        ];
    },
});

export default ZySplitViewCaret;
